/**
 * Ops Agent Console store
 *
 * Drives the Super-Admin "AI Console": lists the ops agents, loads each agent's
 * saved transcript, and streams chat turns over SSE. Streaming uses `fetch`
 * (POST) + a ReadableStream reader rather than `EventSource`, so the bearer
 * token rides in the Authorization header (not the URL) and long messages aren't
 * constrained by URL length. Tool-step events reflect REAL server-side tool runs.
 */
import { create } from 'zustand';
import { getApiClient } from '@respectly/api-client';

const API_URL = import.meta.env.VITE_API_URL ?? '';

export interface OpsAgentSummary {
  key: string;
  label: string;
  description: string;
  icon: string;
  samplePrompts: string[];
}

export interface OpsToolStep {
  id: string;
  serverLabel?: string;
  tool?: string;
  status: 'running' | 'completed' | 'failed';
  summary?: string;
}

export interface OpsChatMessage {
  role: 'user' | 'assistant';
  content: string;
  toolSteps?: OpsToolStep[];
  at?: string;
  streaming?: boolean;
}

interface Conversation {
  messages: OpsChatMessage[];
  status: 'idle' | 'loading' | 'streaming';
  error: string | null;
  loaded: boolean;
}

interface OpsAgentState {
  agents: OpsAgentSummary[];
  agentsLoaded: boolean;
  agentsError: string | null;
  activeAgentKey: string | null;
  conversations: Record<string, Conversation>;
  controllers: Record<string, AbortController>;

  loadAgents: () => Promise<void>;
  selectAgent: (key: string) => void;
  loadConversation: (key: string) => Promise<void>;
  sendMessage: (key: string, message: string, getToken: () => Promise<string | null>) => Promise<void>;
  stop: (key: string) => void;
  clearConversation: (key: string) => Promise<void>;
}

const emptyConversation = (): Conversation => ({
  messages: [],
  status: 'idle',
  error: null,
  loaded: false,
});

/** Parse SSE frames out of a streamed Response body. Yields { event, data }. */
async function* parseSse(
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal
): AsyncGenerator<{ event: string; data: string }> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep: number;
      // Frames are separated by a blank line.
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        let event = 'message';
        const dataLines: string[] = [];
        for (const line of frame.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
          // lines starting with ':' are comments/keepalives — ignore
        }
        if (dataLines.length) yield { event, data: dataLines.join('\n') };
      }
    }
  } finally {
    reader.cancel().catch(() => {});
  }
}

export const useOpsAgentStore = create<OpsAgentState>((set, get) => {
  /** Immutably patch one agent's conversation. */
  const patch = (key: string, updater: (c: Conversation) => Conversation) => {
    set((state) => {
      const current = state.conversations[key] ?? emptyConversation();
      return { conversations: { ...state.conversations, [key]: updater(current) } };
    });
  };

  /** Update the last assistant message in a conversation. */
  const patchLastAssistant = (key: string, updater: (m: OpsChatMessage) => OpsChatMessage) => {
    patch(key, (c) => {
      const messages = [...c.messages];
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i]!.role === 'assistant') {
          messages[i] = updater(messages[i]!);
          break;
        }
      }
      return { ...c, messages };
    });
  };

  return {
    agents: [],
    agentsLoaded: false,
    agentsError: null,
    activeAgentKey: null,
    conversations: {},
    controllers: {},

    async loadAgents() {
      try {
        const agents = await getApiClient().get<OpsAgentSummary[]>('/api/super-admin/ops-agents/agents');
        set((state) => ({
          agents,
          agentsLoaded: true,
          agentsError: null,
          activeAgentKey: state.activeAgentKey ?? agents[0]?.key ?? null,
        }));
      } catch (error) {
        set({ agentsLoaded: true, agentsError: error instanceof Error ? error.message : 'Failed to load agents' });
      }
    },

    selectAgent(key) {
      set({ activeAgentKey: key });
      const convo = get().conversations[key];
      if (!convo?.loaded) void get().loadConversation(key);
    },

    async loadConversation(key) {
      patch(key, (c) => ({ ...c, status: 'loading', error: null }));
      try {
        const data = await getApiClient().get<{ agentKey: string; messages: OpsChatMessage[] }>(
          `/api/super-admin/ops-agents/${key}/conversation`
        );
        patch(key, (c) => ({ ...c, messages: data.messages ?? [], status: 'idle', loaded: true }));
      } catch (error) {
        patch(key, (c) => ({
          ...c,
          status: 'idle',
          loaded: true,
          error: error instanceof Error ? error.message : 'Failed to load conversation',
        }));
      }
    },

    async sendMessage(key, message, getToken) {
      const trimmed = message.trim();
      if (!trimmed) return;
      // Already streaming for this agent? Ignore (composer is disabled in the UI).
      if (get().conversations[key]?.status === 'streaming') return;

      const now = new Date().toISOString();
      patch(key, (c) => ({
        ...c,
        status: 'streaming',
        error: null,
        loaded: true,
        messages: [
          ...c.messages,
          { role: 'user', content: trimmed, at: now },
          { role: 'assistant', content: '', toolSteps: [], at: now, streaming: true },
        ],
      }));

      const controller = new AbortController();
      set((state) => ({ controllers: { ...state.controllers, [key]: controller } }));

      // ── Token coalescing ────────────────────────────────────────────────
      // Decouple token-arrival rate from React render rate: buffer incoming
      // token text and flush to the store at most ~once per animation frame.
      // These are per-call closures (one set per `sendMessage`), so they're
      // naturally scoped to this agent/turn and garbage-collected on return.
      let pending = '';
      let rafId: number | null = null;
      let timerId: ReturnType<typeof setTimeout> | null = null;
      let flushedDone = false;
      const hasRaf = typeof requestAnimationFrame === 'function';
      const MAX_WAIT_MS = 100;

      const clearScheduled = () => {
        if (rafId != null && hasRaf) cancelAnimationFrame(rafId);
        if (timerId != null) clearTimeout(timerId);
        rafId = null;
        timerId = null;
      };
      const flush = () => {
        clearScheduled();
        if (!pending) return;
        const chunk = pending;
        pending = '';
        patchLastAssistant(key, (m) => ({ ...m, content: m.content + chunk }));
      };
      const scheduleFlush = () => {
        if (flushedDone) return;
        // setTimeout fallback fires even when the tab is hidden / rAF is paused.
        if (timerId == null) timerId = setTimeout(flush, MAX_WAIT_MS);
        if (hasRaf && rafId == null) {
          rafId = requestAnimationFrame(() => {
            rafId = null;
            flush();
          });
        }
      };

      try {
        const token = await getToken();
        const res = await fetch(`${API_URL}/api/super-admin/ops-agents/${key}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ message: trimmed }),
          signal: controller.signal,
        });

        if (!res.ok || !res.body) {
          let msg = `Request failed (${res.status})`;
          try {
            const j = await res.json();
            msg = j?.error?.message ?? msg;
          } catch {
            /* non-JSON */
          }
          throw new Error(msg);
        }

        for await (const { event, data } of parseSse(res.body, controller.signal)) {
          if (event === 'token') {
            const { text } = JSON.parse(data) as { text: string };
            pending += text;
            scheduleFlush();
          } else if (event === 'tool_step') {
            // Commit buffered text first so visible content and step cards stay ordered.
            flush();
            const step = JSON.parse(data) as OpsToolStep;
            patchLastAssistant(key, (m) => {
              const steps = [...(m.toolSteps ?? [])];
              const idx = steps.findIndex((s) => s.id === step.id);
              if (idx >= 0) steps[idx] = step;
              else steps.push(step);
              return { ...m, toolSteps: steps };
            });
          } else if (event === 'done') {
            // `text` is the authoritative full text. Flush buffered tokens, then
            // replace wholesale — flush-then-replace means no loss/duplication.
            const { text } = JSON.parse(data) as { text: string };
            flush();
            flushedDone = true;
            clearScheduled();
            patchLastAssistant(key, (m) => ({ ...m, content: text || m.content, streaming: false }));
          } else if (event === 'error') {
            flush(); // preserve whatever partial text already streamed
            flushedDone = true;
            clearScheduled();
            const { message: errMsg } = JSON.parse(data) as { message: string };
            patch(key, (c) => ({ ...c, error: errMsg }));
            patchLastAssistant(key, (m) => ({ ...m, streaming: false }));
          }
        }
      } catch (error) {
        flush(); // persist partial content BEFORE flipping streaming off
        flushedDone = true;
        clearScheduled();
        const aborted = error instanceof DOMException && error.name === 'AbortError';
        patchLastAssistant(key, (m) => ({
          ...m,
          streaming: false,
          content: m.content || (aborted ? '_(stopped)_' : ''),
        }));
        if (!aborted) {
          patch(key, (c) => ({ ...c, error: error instanceof Error ? error.message : 'Agent run failed' }));
        }
      } finally {
        flush(); // idempotent — buffer is already empty on the happy path
        clearScheduled(); // guarantee no leaked rAF/timeout
        patch(key, (c) => ({ ...c, status: 'idle' }));
        patchLastAssistant(key, (m) => ({ ...m, streaming: false }));
        set((state) => {
          const next = { ...state.controllers };
          delete next[key];
          return { controllers: next };
        });
      }
    },

    stop(key) {
      get().controllers[key]?.abort();
    },

    async clearConversation(key) {
      get().controllers[key]?.abort();
      try {
        await getApiClient().delete(`/api/super-admin/ops-agents/${key}/conversation`);
      } catch {
        /* best-effort */
      }
      patch(key, () => ({ ...emptyConversation(), loaded: true }));
    },
  };
});
